import React, { useEffect } from "react";
import { Box, Button, FormControl, Grid, InputLabel, MenuItem, Select, SelectChangeEvent } from "@mui/material";
import { makeStyles } from "@mui/styles";
import FilterListItem from "../FilterListItem/FilterListItem";
import { useGetTechnologiesByNameQuery } from "../../store/apis/search";
import colors from "../../theme/colors";

const useStyles = makeStyles({
  formControl: {
    minWidth: 120,
  },
});

interface TechSubtechFilterProps {
  title?: string;
  name?: string;
  tooltip?: string;
  value?: number[];
  open?: boolean;
  icon?: React.ReactNode;
  setOpen?: (value: boolean) => void;
  onChange: (value: number[]) => void;
}

const TechSubtechFilter: React.FC<TechSubtechFilterProps> = ({
  title = "Technologies",
  name = "technologies-filter",
  tooltip,
  value = [],
  open,
  icon,
  setOpen,
  onChange,
}) => {
  const classes = useStyles();

  const { data: technologiesOptions = [], isLoading: isTechnologiesLoading } = useGetTechnologiesByNameQuery("public_network");

  const [techValue, setTechValue] = React.useState<number>(0);
  const [subTechValue, setSubTechValue] = React.useState<number>(0);

  useEffect(() => {
    let foundTech = technologiesOptions.find(option => option.id === value[0]);

    if (!foundTech) {
      //if not found in main technologies look in subtechnologies
      foundTech = technologiesOptions.find(tech => {
        return tech.parent_id === value[0];
      });
    }

    if (foundTech) {
      // If it's a subtechnology, set the parent technology as selected
      const parentId = foundTech.parent_id;
      if (typeof parentId === "number" && parentId !== 0) {
        setTechValue(parentId);
        setSubTechValue(foundTech.id);
      } else {
        // If it's a main technology
        setTechValue(foundTech.id);
        setSubTechValue(0);
      }
    } else {
      setTechValue(0);
      setSubTechValue(0);
    }
  }, [technologiesOptions, value]);

  const handleChange = (event: SelectChangeEvent) => {
    const newValue = Number(event.target.value);
    setTechValue(newValue);
    setSubTechValue(0);
    onChange([newValue]);
  };

  const handleChange2 = (event: SelectChangeEvent) => {
    const newValue = Number(event.target.value);
    setSubTechValue(newValue);
    // Keep both parent technology and subtechnology selected
    if (newValue === 0) {
      // If clearing subtechnology, keep parent technology
      onChange([techValue]);
    } else {
      // If selecting subtechnology, keep parent technology selected in the dropdown
      // but pass the subtechnology ID to the parent component
      const selectedSubTech = technologiesOptions.find(tech => tech.id === newValue);
      const parentId = selectedSubTech?.parent_id;
      if (typeof parentId === "number" && parentId !== 0) {
        onChange([newValue]);
      }
    }
  };

  const handleReset = () => {
    setTechValue(0);
    setSubTechValue(0);
    onChange([]);
  };

  // Get main technologies (those without parent_id)
  const mainTechnologies = technologiesOptions.filter(tech => tech.parent_id === null);

  // Get subtechnologies for current techValue
  const subtechnologies = technologiesOptions.filter(tech => tech.parent_id === techValue);

  return (
    //@ts-ignore
    <FilterListItem title={title} icon={icon} open={open} tooltip={tooltip} setOpen={setOpen} selected={value.flat().length}>
      <Box marginBottom={4} id="technologies-filter">
        <FormControl variant="filled" className={classes.formControl} disabled={isTechnologiesLoading} fullWidth>
          <InputLabel id="technology">Technology</InputLabel>
          <Select
            labelId="technology"
            id={`${name}-parent`}
            value={techValue.toString()}
            onChange={handleChange}
            data-testid="technologiesDropdown"
          >
            <MenuItem key="m1-" value={0}>
              ---
            </MenuItem>
            {mainTechnologies.map(option => (
              <MenuItem key={`tech${option.id}`} value={option.id}>
                {option.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {value[0] && subtechnologies.length > 0 && (
          <FormControl variant="filled" className={classes.formControl} fullWidth style={{ margin: "10px 0px 10px 0px" }}>
            <InputLabel id="subtechnology">Subtechnology</InputLabel>
            <Select labelId="subtechnology" id={`${name}-child`} value={subTechValue.toString()} onChange={handleChange2}>
              <MenuItem key="m2-" value={0}>
                ---
              </MenuItem>
              {subtechnologies.map(option => (
                <MenuItem key={`subtech${option.id}`} value={option.id}>
                  {option.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <Grid container spacing={2} marginTop={7}>
          <Grid>
            <Button id={`${name}-clear`} variant="outlined" size="small" onClick={handleReset} style={{ backgroundColor: colors.white }}>
              Clear
            </Button>
          </Grid>
        </Grid>
      </Box>
    </FilterListItem>
  );
};

export default TechSubtechFilter;
